import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";

import { takeUntil } from "rxjs/operators";
import { firstValueFrom, Subject } from "rxjs";

import { CoreConfigService } from "@core/services/config.service";
import { ActivatedRoute, Router } from "@angular/router";
import { AuthService } from "../../../util/service/auth.service";
import { LogoService } from "../../../util/service/logo.service";
import { animate, state, style, transition, trigger } from "@angular/animations";

@Component({
    selector: "app-auth-login-v1",
    templateUrl: "./auth-login-v1.component.html",
    styleUrls: ["./auth-login-v1.component.scss"],
    encapsulation: ViewEncapsulation.None,
    animations: [
        trigger("zoomInAnimation", [
            state("initial", style({
                transform: "scale(1)",
                opacity: "1",
            })),
            state("zoomed", style({
                transform: "scale(1.5)",
                opacity: "0",
            })),
            transition("initial => zoomed", animate("300ms ease-in")),
        ]),
    ],
    standalone: false
})
export class AuthLoginV1Component implements OnInit {
  //  Public
  public coreConfig: any;
  public loginForm: UntypedFormGroup;
  public submitted = false;
  public returnUrl: string;
  public passwordTextType: boolean;
  zoomState = "initial";

  website = "https://kinderfreunde.at/";
  email = "office@kinderfreunde-steiermark.at";
  phone = "+43 316 / 82 55 12 -24";

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {CoreConfigService} _coreConfigService
   * @param {FormBuilder} _formBuilder
   * @param _route
   * @param _router
   * @param authService
   * @param logoService
   */
  constructor(
    private _coreConfigService: CoreConfigService,
    private _formBuilder: UntypedFormBuilder,
    private _route: ActivatedRoute,
    private _router: Router,
    private authService: AuthService,
    public logoService: LogoService
  ) {
    this._unsubscribeAll = new Subject();

    // Configure the layout
    this._coreConfigService.config = {
      layout: {
        navbar: {
          hidden: true,
        },
        menu: {
          hidden: true,
        },
        footer: {
          hidden: true,
        },
        customizer: false,
        enableLocalStorage: false,
      },
    };
  }

  // convenience getter for easy access to form fields
  get f() {
    return this.loginForm.controls;
  }

  /**
   * Toggle password
   */
  togglePasswordTextType() {
    this.passwordTextType = !this.passwordTextType;
  }

  /**
   * On Submit
   */
  onSubmit() {
    this.submitted = true;

    this.loginForm.get("email")?.setValue(this.loginForm.get("email")?.value.trim());

    // stop here if form is invalid
    if (this.loginForm.invalid) {
      return;
    }

    // Login
    this.authService.login(this.loginForm.value.email, this.loginForm.value.password).subscribe({
      next: (user) => {
        this.zoomState = "zoomed";
        setTimeout(() => this._router.navigateByUrl(this.returnUrl ?? "/"), 300);
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    firstValueFrom(this.authService.currentUser).then((currentUser) => {
      if (currentUser) {
        this._router.navigate(["/"]);
      }
    });

    this.loginForm = this._formBuilder.group({
      email: ["", [Validators.required, Validators.email]],
      password: ["", Validators.required],
    });

    // get return url from route parameters or default to '/'
    this.returnUrl = this._route.snapshot.queryParams["returnUrl"] || "/";

    // Subscribe to config changes
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((config) => {
      this.coreConfig = config;
    });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }
}
